import { ACOUSTIC_FACTORY_DEFAULTS } from './config';
import type { AcousticCalibrationSnapshot, AcousticCalibrationTag } from './calibrationTypes';
import {
  deriveSilenceBaselineOverrides,
  deriveThresholdOverridesForTag
} from './calibrationThresholdDeriver';

const CALIBRATION_TAG_ORDER: AcousticCalibrationTag[] = [
  'single_speaker',
  'conversation',
  'babble',
  'shouting'
];

export type AcousticThresholdOverrideKey =
  | 'shoutingLoudnessThreshold'
  | 'shoutingCrestFactorMin'
  | 'shoutingLoudnessWithCrestMin'
  | 'shoutingPeakDensityMin'
  | 'babbleDiffusenessMin'
  | 'babbleNoiseIntensityMin'
  | 'babbleTurnTakingMin'
  | 'babbleLoudnessMin'
  | 'babbleLoudnessMax'
  | 'babblePeakDensityMax'
  | 'yamnetBabbleTriggerMin'
  | 'yamnetShoutingTriggerMin'
  | 'roomActivityNoiseMin'
  | 'roomActivityLoudnessMin';

export type AcousticThresholdOverrides = Partial<Record<AcousticThresholdOverrideKey, number>>;

export type AcousticThresholdOverrideStore = {
  version: 1;
  updatedAt: string;
  /** Empirische Schwellen ersetzen Werkseinstellungen app-weit (Live-Unterricht inkl.) */
  empiricalGlobalActive: boolean;
  byTag: Partial<Record<string, AcousticThresholdOverrides>>;
  merged: AcousticThresholdOverrides;
};

export const ACOUSTIC_THRESHOLD_STORAGE_KEY = 'acousticThresholdOverrides.v1';
export const ACOUSTIC_THRESHOLD_UPDATED_EVENT = 'acoustic-threshold-overrides-updated';

const EMPTY_MERGED: AcousticThresholdOverrides = {};

function emptyStore(): AcousticThresholdOverrideStore {
  return {
    version: 1,
    updatedAt: new Date().toISOString(),
    empiricalGlobalActive: false,
    byTag: {},
    merged: { ...EMPTY_MERGED }
  };
}

export function readThresholdOverrideStore(): AcousticThresholdOverrideStore {
  if (typeof window === 'undefined') return emptyStore();
  try {
    const raw = window.localStorage.getItem(ACOUSTIC_THRESHOLD_STORAGE_KEY);
    if (!raw) return emptyStore();
    const parsed = JSON.parse(raw) as AcousticThresholdOverrideStore;
    if (parsed?.version !== 1) return emptyStore();
    return {
      ...emptyStore(),
      ...parsed,
      empiricalGlobalActive: Boolean(parsed.empiricalGlobalActive),
      merged: { ...EMPTY_MERGED, ...parsed.merged }
    };
  } catch {
    return emptyStore();
  }
}

type WriteStoreOptions = { persistRemote?: boolean };

function writeStore(store: AcousticThresholdOverrideStore, options?: WriteStoreOptions): void {
  const next: AcousticThresholdOverrideStore = {
    ...store,
    updatedAt: new Date().toISOString()
  };
  window.localStorage.setItem(ACOUSTIC_THRESHOLD_STORAGE_KEY, JSON.stringify(next));
  window.dispatchEvent(new CustomEvent(ACOUSTIC_THRESHOLD_UPDATED_EVENT));
  if (options?.persistRemote !== false && typeof window !== 'undefined') {
    void import('../acousticCalibrationApi')
      .then(({ saveAcousticThresholdsToServer }) => saveAcousticThresholdsToServer(next))
      .catch(() => {});
  }
}

export function replaceThresholdOverrideStore(
  store: AcousticThresholdOverrideStore,
  options?: WriteStoreOptions
): AcousticThresholdOverrideStore {
  const next: AcousticThresholdOverrideStore = {
    ...emptyStore(),
    ...store,
    empiricalGlobalActive: Boolean(store.empiricalGlobalActive),
    byTag: { ...store.byTag },
    merged: { ...EMPTY_MERGED, ...store.merged }
  };
  writeStore(next, options);
  return next;
}

function mergeAllTagOverrides(byTag: Partial<Record<string, AcousticThresholdOverrides>>): AcousticThresholdOverrides {
  return Object.values(byTag).reduce<AcousticThresholdOverrides>(
    (acc, patch) => ({ ...acc, ...patch }),
    {}
  );
}

export function applyThresholdOverridesForTag(
  tag: string,
  overrides: AcousticThresholdOverrides,
  options?: { activateGlobal?: boolean }
): AcousticThresholdOverrideStore {
  const store = readThresholdOverrideStore();
  store.byTag[tag] = overrides;
  store.merged = mergeAllTagOverrides(store.byTag);
  if (options?.activateGlobal !== false) {
    store.empiricalGlobalActive = true;
  }
  writeStore(store);
  return store;
}

export type ApplyAllEmpiricalResult =
  | {
      ok: true;
      appliedTags: AcousticCalibrationTag[];
      skippedTags: Array<{ tag: AcousticCalibrationTag; reason: string }>;
      merged: AcousticThresholdOverrides;
    }
  | { ok: false; error: string };

/** Alle Kategorien mit genügend Snapshots → eine globale empirische Konfiguration für die ganze App. */
export function applyAllEmpiricalThresholdsFromSnapshots(
  snapshots: AcousticCalibrationSnapshot[]
): ApplyAllEmpiricalResult {
  if (!snapshots.length) {
    return { ok: false, error: 'Keine Kalibrierungs-Snapshots vorhanden.' };
  }

  const store = readThresholdOverrideStore();
  store.byTag = {};
  const appliedTags: AcousticCalibrationTag[] = [];
  const skippedTags: Array<{ tag: AcousticCalibrationTag; reason: string }> = [];

  for (const tag of CALIBRATION_TAG_ORDER) {
    const result = deriveThresholdOverridesForTag(tag, snapshots);
    if (result.ok) {
      store.byTag[tag] = result.overrides;
      appliedTags.push(tag);
    } else {
      skippedTags.push({ tag, reason: result.error });
    }
  }

  const silenceBaseline = deriveSilenceBaselineOverrides(snapshots);
  if (silenceBaseline.ok) {
    store.byTag.silence = silenceBaseline.overrides;
    if (!appliedTags.includes('silence' as AcousticCalibrationTag)) {
      appliedTags.push('silence');
    }
  } else if (snapshots.some((s) => s.tag === 'silence')) {
    skippedTags.push({ tag: 'silence', reason: silenceBaseline.error });
  }

  if (!appliedTags.length) {
    return {
      ok: false,
      error: skippedTags[0]?.reason ?? 'Keine Schwellen ableitbar.'
    };
  }

  store.merged = mergeAllTagOverrides(store.byTag);
  store.empiricalGlobalActive = true;
  writeStore(store);

  return { ok: true, appliedTags, skippedTags, merged: store.merged };
}

export function clearThresholdOverrides(): void {
  writeStore(emptyStore());
}

export function clearThresholdOverridesForTag(tag: string): void {
  const store = readThresholdOverrideStore();
  delete store.byTag[tag];
  store.merged = mergeAllTagOverrides(store.byTag);
  store.empiricalGlobalActive = Object.keys(store.merged).length > 0;
  writeStore(store);
}

export type AcousticRuntimeConfig = {
  [K in keyof typeof ACOUSTIC_FACTORY_DEFAULTS]: (typeof ACOUSTIC_FACTORY_DEFAULTS)[K] extends number
    ? number
    : (typeof ACOUSTIC_FACTORY_DEFAULTS)[K];
};

export function hasEmpiricalThresholds(): boolean {
  const store = readThresholdOverrideStore();
  return store.empiricalGlobalActive && Object.keys(store.merged).length > 0;
}

export function getAcousticConfig(): AcousticRuntimeConfig {
  const store = readThresholdOverrideStore();
  if (store.empiricalGlobalActive && Object.keys(store.merged).length > 0) {
    return { ...ACOUSTIC_FACTORY_DEFAULTS, ...store.merged } as AcousticRuntimeConfig;
  }
  return { ...ACOUSTIC_FACTORY_DEFAULTS } as AcousticRuntimeConfig;
}

export function getEffectiveSituationThresholds(): AcousticThresholdOverrides {
  return getAcousticConfig() as unknown as AcousticThresholdOverrides;
}

export function listActiveThresholdOverrides(): Array<{
  key: AcousticThresholdOverrideKey;
  value: number;
  defaultValue: number;
}> {
  const merged = readThresholdOverrideStore().merged;
  return (Object.keys(merged) as AcousticThresholdOverrideKey[])
    .map((key) => ({
      key,
      value: merged[key]!,
      defaultValue: ACOUSTIC_FACTORY_DEFAULTS[key as keyof typeof ACOUSTIC_FACTORY_DEFAULTS] as number
    }))
    .filter((entry) => entry.value !== entry.defaultValue);
}
